import { useEffect } from "react";
import * as Konva from "react-konva";

import { getPalette, INACTIVE_OPACITY } from "./palette";
import type {
  SimPick,
  SimTask,
  Truck,
  TruckOverflowBin,
} from "./simulator-model";

const FONT_FAMILY = "Public Sans, sans-serif";
const TITLE_FONT_SIZE = 13;
const CRUMB_FONT_SIZE = 12;
const CELL_FONT_SIZE = 10;
const SLOT_FONT_SIZE = 9;
const TITLE_HEIGHT = 32;
const CELL_SIZE = 32;
const CELL_GAP = 2;
const CARD_PADDING_X = 12;
const CARD_PADDING_Y = 12;
const CARD_GAP = 12;
const CORNER_RADIUS = 4;
const OVF_REGION_TOP_GAP = 18;
const OVF_HEADER_HEIGHT = 16;
const OVF_CELL_HEIGHT = 36;
const OVF_GAP = 6;
const OVF_CELL_MAX_WIDTH = 96;
const OVF_OUTER_PADDING = 12;
const INACTIVE_TRUCK_OPACITY = 0.6;
const NORMAL_TRAILER_COLS = 2;
const STACK_BAR_WIDTH = 3;

export type TrucksProps = {
  trucks: Truck[];
  activeTask?: SimTask;
  activePick?: SimPick;
  x: number;
  y: number;
  width: number;
  height: number;
};

type OverflowEntry = {
  bin: TruckOverflowBin;
  truck: Truck;
};

export function Trucks({
  trucks,
  activeTask,
  activePick,
  x,
  y,
  width,
}: TrucksProps) {
  if (trucks.length === 0) return null;

  const allOverflow: OverflowEntry[] = [];
  for (const truck of trucks) {
    for (const bin of truck.overflow) {
      allOverflow.push({ bin, truck });
    }
  }
  const showTruckLabel = trucks.length > 1;
  const activeOverflowKey =
    activePick?.loadLocationKind === "OVERFLOW" ? activePick.palletKey : null;

  let cursorY = 0;
  const cards = trucks.map((truck) => {
    const gridCols = Math.max(NORMAL_TRAILER_COLS, truck.bounds.cols);
    const gridRows = Math.max(1, truck.bounds.rows);
    const gridPixelW = gridCols * CELL_SIZE + (gridCols - 1) * CELL_GAP;
    const gridPixelH = gridRows * CELL_SIZE + (gridRows - 1) * CELL_GAP;
    const cardWidth = Math.max(width, gridPixelW + CARD_PADDING_X * 2);
    const cardHeight =
      TITLE_HEIGHT + CARD_PADDING_Y + gridPixelH + CARD_PADDING_Y;
    const isActive = activePick != null && activePick.routeId === truck.routeId;
    const dim = activePick != null && !isActive;
    const cardY = cursorY;
    cursorY += cardHeight + CARD_GAP;
    return {
      truck,
      gridCols,
      gridRows,
      cardWidth,
      cardHeight,
      cardY,
      activePick: isActive ? activePick : undefined,
      dim,
    };
  });

  const overflowTop =
    allOverflow.length > 0 ? cursorY + OVF_REGION_TOP_GAP : null;

  return (
    <Konva.Group x={x} y={y}>
      {cards.map((card) => (
        <TrailerCard
          key={card.truck.routeId}
          truck={card.truck}
          activeTask={activeTask}
          activePick={card.activePick}
          dim={card.dim}
          x={0}
          y={card.cardY}
          width={width}
          height={card.cardHeight}
          gridCols={card.gridCols}
          gridRows={card.gridRows}
        />
      ))}
      {overflowTop != null ? (
        <SharedOverflow
          entries={allOverflow}
          activePalletKey={activeOverflowKey}
          showTruckLabel={showTruckLabel}
          x={0}
          y={overflowTop}
          width={width}
        />
      ) : null}
    </Konva.Group>
  );
}

type TrailerCardProps = {
  truck: Truck;
  activeTask?: SimTask;
  activePick?: SimPick;
  dim: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
  gridCols: number;
  gridRows: number;
};

function TrailerCard({
  truck,
  activeTask,
  activePick,
  dim,
  x,
  y,
  width,
  height,
  gridCols,
  gridRows,
}: TrailerCardProps) {
  const palette = getPalette();
  const bugCols = Array.from(
    new Set(
      truck.cells.filter((c) => c.col > NORMAL_TRAILER_COLS).map((c) => c.col),
    ),
  ).sort((a, b) => a - b);

  useEffect(() => {
    if (bugCols.length === 0) return;
    // Surface a planner-side bug (picks outside the physical 2-wide trailer
    // layout). Visible in dev tools; not an app-level error path.
    // eslint-disable-next-line no-console
    console.warn(
      `[plan-simulator] Trailer ${truck.routeCode} (${truck.routeId}) has picks at column(s) ${bugCols.join(", ")}, outside the physical 2-wide layout. Likely a planner-side bug.`,
    );
  }, [truck.routeId, truck.routeCode, bugCols]);

  const isEmpty = truck.cells.length === 0 && truck.overflow.length === 0;
  const totalCells = gridCols * gridRows;
  const filledCells = truck.cells.length;
  const gridPixelW = gridCols * CELL_SIZE + (gridCols - 1) * CELL_GAP;
  const gridPixelH = gridRows * CELL_SIZE + (gridRows - 1) * CELL_GAP;
  const gridX = Math.max(CARD_PADDING_X, (width - gridPixelW) / 2);
  const gridY = TITLE_HEIGHT + CARD_PADDING_Y;

  const cellByKey = new Map<string, (typeof truck.cells)[number]>();
  for (const cell of truck.cells) {
    cellByKey.set(`${cell.row}|${cell.col}`, cell);
  }

  const activeCellKey =
    activePick?.loadLocationKind === "POSITIONED" &&
    activePick.row != null &&
    activePick.col != null
      ? `${activePick.row}|${activePick.col}`
      : null;

  const activeTaskCells = new Set<string>();
  if (activeTask) {
    for (const p of activeTask.picks) {
      if (
        p.routeId === truck.routeId &&
        p.loadLocationKind === "POSITIONED" &&
        p.row != null &&
        p.col != null
      ) {
        activeTaskCells.add(`${p.row}|${p.col}`);
      }
    }
  }

  return (
    <Konva.Group x={x} y={y} opacity={dim ? INACTIVE_TRUCK_OPACITY : 1}>
      <Konva.Rect
        x={0}
        y={0}
        width={width}
        height={height}
        stroke={palette.cellStroke}
        strokeWidth={1}
        cornerRadius={CORNER_RADIUS}
        fill={palette.cellFill === "transparent" ? undefined : palette.cellFill}
      />
      <Konva.Text
        x={CARD_PADDING_X}
        y={10}
        text={truck.routeCode}
        fontSize={TITLE_FONT_SIZE}
        fontFamily={FONT_FAMILY}
        fontStyle="bold"
        fill={palette.textTitle}
      />
      <Konva.Text
        x={width - CARD_PADDING_X - 80}
        y={11}
        text={`${filledCells}/${totalCells}`}
        fontSize={CRUMB_FONT_SIZE}
        fontFamily={FONT_FAMILY}
        fill={palette.textMuted}
        width={80}
        align="right"
      />
      <Konva.Line
        points={[0, TITLE_HEIGHT - 1, width, TITLE_HEIGHT - 1]}
        stroke={palette.cellStroke}
        strokeWidth={1}
      />
      {isEmpty ? (
        <Konva.Rect
          x={gridX}
          y={gridY}
          width={gridPixelW}
          height={gridPixelH}
          fill={palette.inactivePanel}
          cornerRadius={CORNER_RADIUS}
        />
      ) : (
        Array.from({ length: gridRows }, (_, r) => r + 1).map((row) =>
          Array.from({ length: gridCols }, (_, c) => c + 1).map((col) => {
            const cell = cellByKey.get(`${row}|${col}`);
            const cx = gridX + (col - 1) * (CELL_SIZE + CELL_GAP);
            const cy = gridY + (row - 1) * (CELL_SIZE + CELL_GAP);
            const isActive = cell != null && activeCellKey === `${row}|${col}`;
            const isBug = col > NORMAL_TRAILER_COLS;
            const stroke = isActive
              ? palette.activeStroke
              : isBug
                ? palette.bugStroke
                : cell
                  ? palette.cellStrokeStrong
                  : palette.emptyStroke;
            const fill = isActive
              ? palette.activeFill
              : cell && isBug
                ? palette.bugFill
                : undefined;
            const strokeWidth = isActive ? palette.activeStrokeWidth : 1;
            const cellKey = `${row}|${col}`;
            const inTaskCell = activeTaskCells.has(cellKey);
            const isActiveCell = activeCellKey === cellKey;
            return (
              <Konva.Group key={cellKey} x={cx} y={cy}>
                <Konva.Rect
                  x={0}
                  y={0}
                  width={CELL_SIZE}
                  height={CELL_SIZE}
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                  fill={fill}
                  cornerRadius={CORNER_RADIUS}
                />
                {cell != null ? (
                  <StackBar
                    stacks={cell.stacks}
                    isActiveCell={isActiveCell}
                    inTaskCell={inTaskCell}
                    activeStackPosition={
                      isActiveCell ? (activePick?.stackPosition ?? null) : null
                    }
                  />
                ) : null}
                {cell != null ? (
                  <Konva.Text
                    x={0}
                    y={0}
                    width={CELL_SIZE}
                    height={CELL_SIZE}
                    text={`${col},${row}`}
                    fontSize={CELL_FONT_SIZE}
                    fontFamily={FONT_FAMILY}
                    fontStyle={isActive ? "bold" : "normal"}
                    align="center"
                    verticalAlign="middle"
                    fill={palette.textTitle}
                  />
                ) : null}
              </Konva.Group>
            );
          }),
        )
      )}
    </Konva.Group>
  );
}

type StackBarProps = {
  stacks: { stackPosition: number; pick: SimPick }[];
  isActiveCell: boolean;
  inTaskCell: boolean;
  activeStackPosition: number | null;
};

function StackBar({
  stacks,
  isActiveCell,
  inTaskCell,
  activeStackPosition,
}: StackBarProps) {
  const palette = getPalette();
  const n = stacks.length;
  if (n === 0) return null;
  const segH = CELL_SIZE / n;

  // Stacks are sorted ascending by stackPosition (floor → ceiling); lower index
  // sits lower on screen, so we draw from bottom up.
  return (
    <Konva.Group x={0} y={0}>
      {stacks.map((stack, idx) => {
        const isActiveSegment =
          isActiveCell && activeStackPosition === stack.stackPosition;
        const segY = CELL_SIZE - (idx + 1) * segH;
        const fill = isActiveSegment
          ? palette.markerActive
          : isActiveCell || inTaskCell
            ? palette.markerInTask
            : palette.markerInactive;
        const opacity = isActiveCell || inTaskCell ? 1 : INACTIVE_OPACITY;
        return (
          <Konva.Rect
            key={stack.stackPosition}
            x={0}
            y={segY}
            width={STACK_BAR_WIDTH}
            height={segH}
            fill={fill}
            opacity={opacity}
          />
        );
      })}
      {Array.from({ length: n - 1 }, (_, i) => {
        const dividerY = CELL_SIZE - (i + 1) * segH;
        return (
          <Konva.Line
            key={`divider-${i}`}
            points={[0, dividerY, STACK_BAR_WIDTH, dividerY]}
            stroke={palette.cellStroke}
            strokeWidth={1}
          />
        );
      })}
    </Konva.Group>
  );
}

type SharedOverflowProps = {
  entries: OverflowEntry[];
  activePalletKey: string | null;
  showTruckLabel: boolean;
  x: number;
  y: number;
  width: number;
};

function SharedOverflow({
  entries,
  activePalletKey,
  showTruckLabel,
  x,
  y,
  width,
}: SharedOverflowProps) {
  const palette = getPalette();
  const n = entries.length;
  const innerWidth = width - OVF_OUTER_PADDING * 2;
  const binWidth = Math.max(
    0,
    Math.min(OVF_CELL_MAX_WIDTH, (innerWidth - OVF_GAP * (n - 1)) / n),
  );
  const cardHeight = OVF_OUTER_PADDING * 2 + OVF_CELL_HEIGHT;

  return (
    <Konva.Group x={x} y={y}>
      <Konva.Text
        x={0}
        y={0}
        text="OVERFLOW"
        fontSize={11}
        fontFamily={FONT_FAMILY}
        fill={palette.textMuted}
      />
      <Konva.Rect
        x={0}
        y={OVF_HEADER_HEIGHT}
        width={width}
        height={cardHeight}
        stroke={palette.cellStroke}
        strokeWidth={1}
        cornerRadius={CORNER_RADIUS}
      />
      <Konva.Group
        x={OVF_OUTER_PADDING}
        y={OVF_HEADER_HEIGHT + OVF_OUTER_PADDING}
      >
        {entries.map((entry, idx) => {
          const isActive = entry.bin.palletKey === activePalletKey;
          return (
            <Konva.Group
              key={`${entry.truck.routeId}|${entry.bin.palletKey}`}
              x={idx * (binWidth + OVF_GAP)}
              y={0}
            >
              <Konva.Rect
                x={0}
                y={0}
                width={binWidth}
                height={OVF_CELL_HEIGHT}
                stroke={
                  isActive ? palette.activeStroke : palette.cellStrokeStrong
                }
                strokeWidth={isActive ? palette.activeStrokeWidth : 1}
                fill={isActive ? palette.activeFill : undefined}
                cornerRadius={CORNER_RADIUS}
              />
              {showTruckLabel ? (
                <Konva.Text
                  x={4}
                  y={4}
                  width={Math.max(0, binWidth - 8)}
                  text={entry.truck.routeCode}
                  fontSize={CELL_FONT_SIZE}
                  fontFamily={FONT_FAMILY}
                  align="center"
                  fill={palette.textTitle}
                  ellipsis
                  wrap="none"
                />
              ) : null}
              <Konva.Text
                x={4}
                y={showTruckLabel ? 18 : 12}
                width={Math.max(0, binWidth - 8)}
                text={entry.bin.palletKey}
                fontSize={SLOT_FONT_SIZE}
                fontFamily={FONT_FAMILY}
                align="center"
                fill={palette.textMuted}
                ellipsis
                wrap="none"
              />
            </Konva.Group>
          );
        })}
      </Konva.Group>
    </Konva.Group>
  );
}
